import type { ILanguageResources, ITranslationModule } from '../../interfaces';
import { interpolate } from '../../global';

// Logical area: PricingModel General Strings
class Id {
  private constructor() {}
  public static readonly prefix = 'UI.webmodule-common.pricing.PricingModel.';

  public static readonly supplier = `${Id.prefix}supplier`;
  public static readonly margin = `${Id.prefix}margin`;
  public static readonly markup = `${Id.prefix}markup`;
  public static readonly unknown = `${Id.prefix}unknown`;
  public static readonly minMargin = `${Id.prefix}minMargin`;
  public static readonly maxMargin = `${Id.prefix}maxMargin`;
  public static readonly maxMargin100 = `${Id.prefix}maxMargin100`;
  public static readonly minMarkup = `${Id.prefix}minMarkup`;
  public static readonly maxMarkup = `${Id.prefix}maxMarkup`;
  public static readonly minPercent = `${Id.prefix}minPercent`;
  public static readonly minMaxPercent = `${Id.prefix}minMaxPercent`;
  public static readonly refPercentageMarginMarkupFieldWithName = `${Id.prefix}refPercentageMarginMarkupFieldWithName`;
  public static readonly refPercentageMarginMarkupField = `${Id.prefix}refPercentageMarginMarkupField`;
  public static readonly refSupplierMarginRestrictionMsg = `${Id.prefix}refSupplierMarginRestrictionMsg`;
  public static readonly refSupplierMarkupRestrictionMsg = `${Id.prefix}refSupplierMarkupRestrictionMsg`;
}

export class LangPricingModel {
  private constructor() {}

  public static supplier(): string {
    return interpolate(Id.supplier);
  }
  public static margin(): string {
    return interpolate(Id.margin);
  }
  public static markup(): string {
    return interpolate(Id.markup);
  }
  public static unknown(): string {
    return interpolate(Id.unknown);
  }
  public static minMargin(): string {
    return interpolate(Id.minMargin);
  }
  public static maxMargin(): string {
    return interpolate(Id.maxMargin);
  }
  public static maxMargin100(): string {
    return interpolate(Id.maxMargin100);
  }
  public static minMarkup(): string {
    return interpolate(Id.minMarkup);
  }
  public static maxMarkup(): string {
    return interpolate(Id.maxMarkup);
  }
  public static minPercent(min: number): string {
    return interpolate(Id.minPercent, { min });
  }
  public static minMaxPercent(min: number, max: number): string {
    return interpolate(Id.minMaxPercent, { min, max });
  }
  public static refPercentageMarginMarkupFieldWithName(displayFieldName: string): string {
    return interpolate(Id.refPercentageMarginMarkupFieldWithName, { displayFieldName });
  }
  public static refPercentageMarginMarkupField(): string {
    return interpolate(Id.refPercentageMarginMarkupField);
  }
  public static refSupplierMarginRestrictionMsg(supplierName: string, displayField: string, subRange: string): string {
    return interpolate(Id.refSupplierMarginRestrictionMsg, { supplierName, displayField, subRange });
  }
  public static refSupplierMarkupRestrictionMsg(supplierName: string, displayField: string, subRange: string): string {
    return interpolate(Id.refSupplierMarkupRestrictionMsg, { supplierName, displayField, subRange });
  }
}

export class PricingModelModule implements ITranslationModule {
  registerItems(register: ILanguageResources) {
    // General
    register.add(Id.supplier, "{{glossary 'supplier'}}", 'Supplier label');
    register.add(Id.margin, 'Margin', 'Margin label');
    register.add(Id.markup, 'Markup', 'Markup label');
    register.add(Id.unknown, 'Unknown', 'Unknown label');
    register.add(Id.minMargin, 'Minimum Margin value cannot be less than 0%', 'Validation for minimum margin');
    register.add(
      Id.maxMargin,
      'Maximum Margin value cannot be less than or equal to Minimum Margin',
      'Validation for max margin'
    );
    register.add(Id.maxMargin100, 'Maximum Margin value must be less than 100%', 'Validation for max margin 100');
    register.add(Id.minMarkup, 'Minimum Markup value cannot be less than 0%', 'Validation for minimum markup');
    register.add(Id.maxMarkup, 'Maximum Markup value cannot be less than Minimum Markup', 'Validation for max markup');
    register.add(Id.minPercent, '{{min}}%', 'Minimum percent string');
    register.add(Id.minMaxPercent, '{{min}}%-{{max}}%', 'Min-max percent string');
    register.add(
      Id.refPercentageMarginMarkupFieldWithName,
      'the value of "{{displayFieldName}}"',
      'Field name for margin/markup with name'
    );
    register.add(Id.refPercentageMarginMarkupField, 'the value', 'Field name for margin/markup');
    register.add(
      Id.refSupplierMarginRestrictionMsg,
      '{{supplierName}} has restricted {{displayField}} between {{subRange}}, please set a valid value.',
      'Supplier margin restriction message'
    );
    register.add(
      Id.refSupplierMarkupRestrictionMsg,
      '{{supplierName}} has restricted {{displayField}} from {{subRange}}, please set a valid value.',
      'Supplier markup restriction message'
    );
  }
}
